import { canAccessWebLocalStorage } from '@mote/helpers/browser'
import {
  ALL_AUTH_PARAMS,
  CANDIDATE_EXTENSIONS,
  GOOGLE_CLIENT_ID,
  MANDATORY_AUTH_PARAMS,
  OAUTH_SCOPES,
  REDIRECT_URI,
  REFERRER_KEY,
  STORAGE_TIMESTAMP,
  WEB_STORAGE_PREFIX_KEYS
} from '@mote/helpers/constants'
import { getOrMakeAnonymousUserId } from '@mote/helpers/identity/getOrMakeAnonymousUserId'
import { reallyTruthy } from '@mote/helpers/validation'
import { getReferrer, identify } from '../shared/AnalyticsHelpers'
import { log as _log } from '../shared/Utils'

function log(m, ...o) {
  const prefix = 'Auth/' + m
  _log(prefix, ...o)
}

const EXTENSION_FOUND_CALLBACKS = []

const AUTH_LISTENERS = { success: [], failure: [] }

const hasLocalStoragePermission = canAccessWebLocalStorage()

export const getDeviceIdOrAccessTokenHash = () => {
  const o = readAllDataInStorage()
  if (o.accessToken) {
    return { accessToken: getAccessToken() }
  } else {
    if (!o.anonymousUserId) {
      const { anonymousUserId, anonymousCreatedAt } = getOrMakeAnonymousUserId()
      o.anonymousUserId = anonymousUserId
      o.anonymousCreatedAt = anonymousCreatedAt
    }
    return { deviceId: o.anonymousUserId }
  }
}

export const addExtensionFoundListener = f => {
  EXTENSION_FOUND_CALLBACKS.push(f)
}

export const identifyMaybe = () => {
  const o = readAllDataInStorage()
  if (o.userId && o.accessToken && o.primaryEmailAddress && o.name) {
    identify(o.userId, o)
  }
  return o
}

function _broadcastLoginWithDecoration(o) {
  if (o) {
    var oo = Object.assign({}, o)
    broadcastToExtensions('login', oo)
  }
}

export const identifyMaybeAndBroadcast = () => {
  const o = identifyMaybe()
  _broadcastLoginWithDecoration(o)
}

export const readAllDataInStorage = () => {
  const o = {}

  if (hasLocalStoragePermission) {
    for (let key in ALL_AUTH_PARAMS) {
      o[key] = window.localStorage.getItem(WEB_STORAGE_PREFIX_KEYS + key)
      if (
        ['productStates', 'institutionNameHints', 'goals'].includes(key) ||
        /^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$/.test(o[key])
      ) {
        try {
          const value = JSON.parse(o[key])
          o[key] = value
        } catch (e) {}
      } else if (o[key] === 'true') {
        o[key] = true
      } else if (o[key] === 'false') {
        o[key] = false
      }
    }
    o[REFERRER_KEY] = getReferrer()
  }

  return o
}

function _persistDataToStorage(o) {
  if (!hasLocalStoragePermission) return

  for (const key in ALL_AUTH_PARAMS) {
    if (!['anonymousUserId', 'anonymousCreatedAt'].includes(key)) {
      let value = o[key]
      if (value === undefined || value === null) continue

      if (['productStates', 'institutionNameHints', 'goals'].includes(key)) {
        value = JSON.stringify(value)
      }

      window.localStorage.setItem(WEB_STORAGE_PREFIX_KEYS + key, value)

      if (key === 'userId') {
        window.localStorage.setItem(
          `${WEB_STORAGE_PREFIX_KEYS}lastUserId`,
          value
        )
      }
    }
  }

  window.localStorage.setItem(STORAGE_TIMESTAMP, Date.now() / 1000)
}

export function deleteAllDataInStorage() {
  for (var key in ALL_AUTH_PARAMS) {
    if (
      ![
        'anonymousUserId',
        'anonymousCreatedAt',
        'acquiredScopes',
        'lastUserId'
      ].includes(key)
    ) {
      window.localStorage.removeItem(WEB_STORAGE_PREFIX_KEYS + key)
    }
  }
}

export function _persistBroadcastIdentify(data, skipIdentify = false) {
  log('_persistBroadcastIdentify: sending this data to storage', data)
  _persistDataToStorage(data)
  _broadcastLoginWithDecoration(data)

  if (skipIdentify) return

  identify(data.userId, data)
}

export function responseIncludesAuth(o) {
  for (const key in MANDATORY_AUTH_PARAMS) {
    if (!o[key]) {
      return false
    }
  }
  return true
}

export function notifyAuthListeners(callback, isSuccess, meta = null) {
  callback(meta)
  var listeners
  if (isSuccess) {
    listeners = AUTH_LISTENERS.success
  } else {
    listeners = AUTH_LISTENERS.failure
  }
  // log(`we found these listeners on ${isSuccess}: ${listeners}`);
  for (const f of listeners) {
    try {
      f()
    } catch (e) {}
  }
}

export const probe = () => {
  broadcastToExtensions('probe', {})
}

export const broadcastPreferredLocale = selectedLanguage => {
  broadcastToExtensions('preferred_locale', {
    preferredLocale: selectedLanguage
  })
}

export const broadcastLastLoginHostname = () => {
  broadcastToExtensions('last_login_hostname', {
    lastLoginHostname: window.location.hostname
  })
}

export const broadcastExternalWebsiteRedirection = redirectionInfo => {
  broadcastToExtensions('redirect_to', redirectionInfo)
}

export const checkWildCardOriginsPermissions = () => {
  broadcastToExtensions('check_wildcard_origins_permissions')
}

export const requestWildCardOriginsPermissions = () => {
  broadcastToExtensions('request_wildcard_origins_permissions')
}

export function broadcastToExtensions(event, o) {
  if (!window.chrome) return

  for (let extensionId of CANDIDATE_EXTENSIONS) {
    try {
      const sendData = Object.assign({}, o, { extensionId: extensionId })
      window.chrome.runtime?.sendMessage(
        extensionId,
        { type: event, data: sendData },
        response => {
          if (response && response.extensionId) {
            for (let f of EXTENSION_FOUND_CALLBACKS) {
              try {
                f(response)
              } catch (e) {}
            }
          }
        }
      )
    } catch (e) {}
  }
}

export const getMoteLogin = () => {
  return readAllDataInStorage()
}

export const setAccessToken = newAccessToken => {
  if (!hasLocalStoragePermission) return

  window.localStorage.setItem(
    `${WEB_STORAGE_PREFIX_KEYS}accessToken`,
    newAccessToken
  )
}

export const getAccessToken = () => {
  var o = readAllDataInStorage()
  if (o.accessToken) {
    return o.accessToken
  } else {
    return ''
  }
}

export const getUserId = () => {
  var o = readAllDataInStorage()
  if (o.userId) {
    return o.userId
  } else {
    return ''
  }
}

export const getEmail = () => {
  var o = readAllDataInStorage()
  if (o.primaryEmailAddress) {
    return o.primaryEmailAddress
  } else {
    return ''
  }
}

export const getProductEnum = () => {
  var o = readAllDataInStorage()
  try {
    if (o.productStates && o.productStates[0].productEnum) {
      return o.productStates[0].productEnum
    }
  } catch (e) {}
  return ''
}

export const isPaid = () => {
  var o = readAllDataInStorage()
  try {
    if (o.productStates && o.productStates[0].isPaid) {
      return o.productStates[0].isPaid
    }
  } catch (e) {}
  return false
}

export const getUsersName = () => {
  var o = readAllDataInStorage()
  if (o.name) {
    return o.name
  } else {
    return ''
  }
}

export const getUsersGivenName = () => {
  var o = readAllDataInStorage()
  if (o.givenName) {
    return o.givenName
  } else {
    return ''
  }
}

export const getUsersFamilyName = () => {
  var o = readAllDataInStorage()
  if (o.familyName) {
    return o.familyName
  } else {
    return ''
  }
}

export const getUsersEmail = () => {
  var o = readAllDataInStorage()
  if (o.primaryEmailAddress) {
    return o.primaryEmailAddress
  } else {
    return ''
  }
}

export const isMinor = () => {
  var o = readAllDataInStorage()
  return reallyTruthy(o.isMinor)
}

export const getOAuthURL = state => {
  const baseUrl = 'https://accounts.google.com/o/oauth2/auth?'
  const params = {
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    response_type: 'code',
    scope: OAUTH_SCOPES.join(' '),
    access_type: 'offline',
    prompt: 'consent',
    state: new URLSearchParams(state)?.toString()
  }
  return `${baseUrl}${new URLSearchParams(params)?.toString()}`
}

export const getReferrerCodeURL = alternativeReferrerCode => {
  var o = readAllDataInStorage()
  const rc = o.referrerCode || alternativeReferrerCode
  if (rc && rc.length > 0 && rc.length < 10) {
    return window.location.protocol + '//' + window.location.host + '/r/' + rc
  } else {
    console.error('no referrer code: ', alternativeReferrerCode)
    return ''
  }
}
